import { rgba } from 'polished';
import { ColorScheme } from 'shared/assets/styles/theme/color-schemes';
import { typographyToCss } from 'shared/lib/mui';
import { QueryNavLink } from 'shared/lib/react-router-dom';
import styled, { css } from 'styled-components';

const LinkBase = css<{ $prominent?: boolean }>`
  ${({ theme }) => typographyToCss(theme.typography.body2)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};

  min-width: 0;
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};

  border-radius: ${({ theme }) => theme.spacing(2.5)};

  color: ${({ theme, $prominent }) =>
    $prominent
      ? theme.palette.primaryExtended.opacityBackgroundText
      : rgba(
          theme.palette.text.primary,
          theme.colorScheme === ColorScheme.Dark ? 0.6 : 0.8,
        )};

  &:focus {
    outline: none;
    background: ${({ theme }) => rgba(theme.palette.text.primary, 0.06)};
  }
`;

const activeStyles = css`
  ${({ theme }) => typographyToCss(theme.typography.subtitle2)};
  background: ${({ theme }) => rgba(theme.palette.text.primary, 0.06)};
  color: ${({ theme }) => theme.palette.text.primary};

  &:focus {
    background: ${({ theme }) => rgba(theme.palette.text.primary, 0.06)};
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

const hoverStyles = css`
  &:hover {
    background: ${({ theme }) => rgba(theme.palette.text.primary, 0.06)};
  }
`;

const interactiveStyles = css<{
  $active: boolean;
  $isMoving?: boolean;
  $dimmed?: boolean;
}>`
  opacity: ${({ $isMoving, $dimmed }) => ($isMoving || $dimmed ? 0.5 : 1)};

  ${({ $active }) => $active && activeStyles};
  ${({ $active }) => !$active && hoverStyles};
`;

export const DisabledLink = styled.span<{
  $isMoving?: boolean;
  $prominent?: boolean;
  $dimmed?: boolean;
}>`
  ${LinkBase};
  opacity: ${({ $isMoving, $dimmed }) => ($isMoving || $dimmed ? 0.5 : 1)};

  ${({ $prominent }) =>
    $prominent &&
    css`
      &:hover {
        background: ${({ theme }) => rgba(theme.palette.text.primary, 0.06)};
      }
    `};
`;

export const Link = styled(QueryNavLink)<{
  $active: boolean;
  $isMoving?: boolean;
  $prominent?: boolean;
  $dimmed?: boolean;
}>`
  ${LinkBase};
  text-decoration: none;

  ${interactiveStyles};
`;

export const Button = styled.button<{
  $active: boolean;
  $isMoving?: boolean;
  $prominent?: boolean;
  $dimmed?: boolean;
}>`
  ${LinkBase};

  appearance: none;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;

  ${interactiveStyles};
`;

// Opacity on the element, not alpha on the color: an svg paints each stroke
// separately, so an alpha colour compounds wherever strokes overlap.
export const SubItemIcon = styled.span<{
  $prominent?: boolean;
}>`
  color: ${({ theme, $prominent }) =>
    $prominent
      ? theme.palette.primaryExtended.opacityBackgroundText
      : theme.palette.text.primary};
  opacity: ${({ $prominent }) => ($prominent ? 1 : 0.7)};
  font-size: 1.6rem;
  line-height: 62.5%;
`;

export const LinkTitle = styled.span`
  flex: 1;

  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const EndContainer = styled.span`
  display: flex;
  align-items: center;
`;

export const Content = styled.span`
  line-height: 62.5%;
`;

export const Action = styled.span<{ $alwaysVisible?: boolean }>`
  ${({ $alwaysVisible, theme }) =>
    $alwaysVisible
      ? css`
          margin-left: ${theme.spacing(1)};
        `
      : css`
          width: 0;
          opacity: 0;
          margin-left: 0;
          overflow: hidden;

          transition:
            width 200ms,
            opacity 200ms,
            margin-left 200ms;

          ${Link}:hover & {
            width: 2.2rem;
            opacity: 1;
            margin-left: ${theme.spacing(1)};
          }
        `};
`;

export const End = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.grey[600]};
  margin-left: ${({ theme }) => theme.spacing(1)};
  margin-right: ${({ theme }) => theme.spacing(-1.5)};
`;
